export type ReaderMode = 'paged' | 'scroll'

export type Footnote = {
  id: string
  html: string
}

export type PreparedArticle = {
  html: string
  pages: string[]
  footnotes: Record<string, Footnote>
}

const FOOTNOTE_DEFINITION = /^\s*\[(?:\^)?(\d+)\]\s*:?[\s\u00a0]*(.+)$/s
const FOOTNOTE_REFERENCE = /\[(?:\^)?(\d+)\]/g

function textWeight(node: Element) {
  const text = (node.textContent || '').replace(/\s+/g, '')
  const media = node.querySelectorAll('img,table,pre,blockquote,figure').length
  return text.length + media * 260
}

function decorateReferences(root: HTMLElement, footnotes: Record<string, Footnote>) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const textNodes: Text[] = []
  while (walker.nextNode()) {
    const node = walker.currentNode as Text
    const parent = node.parentElement
    if (!parent || parent.closest('button,a,code,pre,ruby,rt,script,style')) continue
    if (FOOTNOTE_REFERENCE.test(node.data)) textNodes.push(node)
    FOOTNOTE_REFERENCE.lastIndex = 0
  }

  textNodes.forEach(node => {
    const fragment = document.createDocumentFragment()
    let cursor = 0
    node.data.replace(FOOTNOTE_REFERENCE, (match, id: string, offset: number) => {
      if (!footnotes[id]) return match
      fragment.append(node.data.slice(cursor, offset))
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'reader-footnote-ref'
      button.dataset.footnote = id
      button.setAttribute('aria-label', `查看注释 ${id}`)
      button.textContent = `[${id}]`
      fragment.append(button)
      cursor = offset + match.length
      return match
    })
    fragment.append(node.data.slice(cursor))
    node.replaceWith(fragment)
  })
}

function decorateRubyAndEmphasis(root: HTMLElement) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const nodes: Text[] = []
  while (walker.nextNode()) {
    const node = walker.currentNode as Text
    const parent = node.parentElement
    if (!parent || parent.closest('a,code,pre,ruby,rt,script,style')) continue
    if (/\{\{[^{}|]+\|[^{}|]+\}\}|\*\*[^*]+\*\*|~~[^~]+~~/.test(node.data)) nodes.push(node)
  }

  nodes.forEach(node => {
    const template = document.createElement('template')
    const escaped = node.data
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\{\{([^{}|]+)\|([^{}|]+)\}\}/g, '<ruby>$1<rt>$2</rt></ruby>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/~~([^~]+)~~/g, '<del>$1</del>')
    template.innerHTML = escaped
    node.replaceWith(template.content)
  })
}

export function prepareArticle(html: string, targetCharacters = 1200): PreparedArticle {
  if (typeof document === 'undefined') return { html, pages: [html], footnotes: {} }
  const wrapper = document.createElement('div')
  wrapper.innerHTML = html || ''
  const footnotes: Record<string, Footnote> = {}
  const children = Array.from(wrapper.children)

  for (let index = children.length - 1; index >= 0; index -= 1) {
    const element = children[index] as HTMLElement
    const match = (element.textContent || '').match(FOOTNOTE_DEFINITION)
    if (!match) {
      if (Object.keys(footnotes).length) break
      continue
    }
    const id = match[1]
    const clone = element.cloneNode(true) as HTMLElement
    clone.innerHTML = clone.innerHTML.replace(/^\s*\[(?:\^)?\d+\]\s*:?\s*/, '')
    footnotes[id] = { id, html: clone.innerHTML || match[2] }
    element.remove()
  }

  decorateRubyAndEmphasis(wrapper)
  decorateReferences(wrapper, footnotes)
  wrapper.querySelectorAll('img').forEach(image => {
    image.setAttribute('loading', 'lazy')
    image.setAttribute('decoding', 'async')
  })

  const pages: string[] = []
  let current: string[] = []
  let weight = 0
  Array.from(wrapper.children).forEach(element => {
    const nextWeight = Math.max(1, textWeight(element))
    if (current.length && weight + nextWeight > targetCharacters) {
      pages.push(current.join(''))
      current = []
      weight = 0
    }
    current.push(element.outerHTML)
    weight += nextWeight
  })
  if (current.length) pages.push(current.join(''))
  if (!pages.length) pages.push(wrapper.innerHTML)

  return { html: wrapper.innerHTML, pages, footnotes }
}

export function stableCoverPalette(id: string) {
  const palettes = [
    ['#1d3029', '#a98a54', '#efe4ca'],
    ['#3a2430', '#c06a8e', '#fff0f6'],
    ['#20284a', '#8c83d8', '#f1efff'],
    ['#3b3020', '#c2a05f', '#fff5d8'],
    ['#18343b', '#69aebc', '#e9fbff']
  ]
  let hash = 0
  for (const character of id) hash = (hash * 31 + character.charCodeAt(0)) >>> 0
  return palettes[hash % palettes.length]
}
